import { GenericTable } from "@shared/components/GenericTable";
import { DeleteConfirmationDialog } from "@shared/components/deleteComformationModal";
import { TableColumn } from "@shared/types/table";
import { PAGE_SIZE } from "@shared/utils/constants";
import { getStatusBadgeClass } from "@shared/utils/statusUtils";
import { AlertTriangle } from "lucide-react";
import React, { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useCancelServiceOrders,
  useServiceOrdersList,
  useServiceOrderSummary,
} from "../../../hooks";
import {
  ServiceOrderDisplayItem,
  ServiceOrderFilterPanelFilters,
} from "../../../types";
import { ServiceOrdersFilterPanel } from "../filters/filters";
import ServiceOrderDashboard from "./dashboard/ServiceOrderDashboard";

const getPriorityColor = (priority: string) => {
  switch (priority.toLowerCase()) {
    case "high":
    case "critical":
      return "bg-red-100 text-red-800 border-red-200";
    case "medium":
    case "normal":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "low":
      return "bg-green-100 text-green-800 border-green-200";
    case "undefined":
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

export const ServiceOrdersTab: React.FC = () => {
  const navigate = useNavigate();
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = PAGE_SIZE;
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [orderToDelete, setOrderToDelete] =
    useState<ServiceOrderDisplayItem | null>(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [ordersToDelete, setOrdersToDelete] = useState<
    ServiceOrderDisplayItem[]
  >([]);

  // Extract current params from URL - Following systems pattern
  const currentSearch = decodeURIComponent(searchParams.get("search") || "");
  const currentStatus = searchParams.get("status") || "";
  const currentPriority = searchParams.get("priority") || "";
  const currentOrderType = searchParams.get("orderType") || "";
  const currentAssignedTo = searchParams.get("assignedTo") || "";
  const currentDateFrom = searchParams.get("dateFrom") || "";
  const currentDateTo = searchParams.get("dateTo") || "";

  // Use individual parameters instead of filter object - Following systems pattern
  const { data: serviceOrdersResponse } = useServiceOrdersList(
    currentSearch,
    currentStatus,
    currentPriority,
    currentOrderType,
    currentAssignedTo,
    currentPage,
    PAGE_SIZE
  );

  const { data: summaryData, isLoading: summaryLoading } =
    useServiceOrderSummary();
  const { mutate: cancelServiceOrdersMutation, isPending: isCancellingOrders } =
    useCancelServiceOrders();

  // Build currentFilters object for filter panel - Following systems pattern
  const currentFilters = useMemo(
    () => ({
      status: currentStatus,
      priority: currentPriority,
      assignedTo: currentAssignedTo,
      orderType: currentOrderType,
      dateRange:
        currentDateFrom && currentDateTo
          ? {
              from: new Date(currentDateFrom),
              to: new Date(currentDateTo),
            }
          : undefined,
    }),
    [
      currentStatus,
      currentPriority,
      currentAssignedTo,
      currentOrderType,
      currentDateFrom,
      currentDateTo,
    ]
  );

  // Pagination info from API or calculate from data
  const pagination = useMemo(() => {
    const totalItems = serviceOrdersResponse?.count;
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const startItem = totalItems > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0;
    const endItem = Math.min(currentPage * itemsPerPage, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage,
      startItem,
      endItem,
    };
  }, [currentPage, serviceOrdersResponse]);

  // Handlers - Following systems pattern
  const handleSearch = (searchTerm: string) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    if (searchTerm.trim()) {
      newParams.search = encodeURIComponent(searchTerm.trim());
    } else {
      delete newParams.search;
    }
    newParams.page = 1;
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    newParams.page = page.toString();
    setSearchParams(newParams);
    setCurrentPage(page);
  };

  const handleApplyFilters = (filters: ServiceOrderFilterPanelFilters) => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };

    // Update URL params like SystemsTable
    if (filters.status && filters.status !== "all") {
      newParams.status = filters.status;
    } else {
      delete newParams.status;
    }

    if (filters.priority && filters.priority !== "all") {
      newParams.priority = filters.priority;
    } else {
      delete newParams.priority;
    }

    if (filters.assignedTo && filters.assignedTo !== "all") {
      newParams.assignedTo = filters.assignedTo;
    } else {
      delete newParams.assignedTo;
    }

    if (filters.orderType && filters.orderType !== "all") {
      newParams.orderType = filters.orderType;
    } else {
      delete newParams.orderType;
    }

    if (filters.dateRange?.from) {
      newParams.dateFrom = filters.dateRange.from.toISOString().split("T")[0];
    } else {
      delete newParams.dateFrom;
    }

    if (filters.dateRange?.to) {
      newParams.dateTo = filters.dateRange.to.toISOString().split("T")[0];
    } else {
      delete newParams.dateTo;
    }

    newParams.page = 1;
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    const newParams: any = { ...Object.fromEntries(searchParams.entries()) };
    delete newParams.status;
    delete newParams.priority;
    delete newParams.assignedTo;
    delete newParams.orderType;
    delete newParams.dateFrom;
    delete newParams.dateTo;
    delete newParams.search;
    newParams.page = 1;
    setSearchParams(newParams);
    setCurrentPage(1);
  };

  const handleExport = () => {
    setIsExporting(true);
    // Implement export logic here
    setTimeout(() => setIsExporting(false), 1000);
  };

  // Function to handle edit navigation with data
  const handleEditOrder = (row: ServiceOrderDisplayItem) => {
    if (row.status.toLowerCase() === "created") {
      return; // Do nothing for Created status
    }
    navigate(`/ax/service-order/landing/edit/${row.id}`, {
      state: { serviceOrder: row },
    });
  };

  const getServiceTypeFromAssociation = (
    sopAssociationTypeDisplay: string
  ): string => {
    console.log("Service Association:", sopAssociationTypeDisplay);
    const association = sopAssociationTypeDisplay;

    if (association?.includes("Asset")) return "Asset";
    if (association?.includes("Facility")) return "Facility";
    if (association?.includes("System")) return "System";
    if (association?.includes("Network")) return "Network";

    // Default fallback
    return "Asset";
  };

  const handleViewDetails = (row: ServiceOrderDisplayItem) => {
    const serviceType = getServiceTypeFromAssociation(
      row.sopAssociationTypeDisplay
    );
    const status = row.status?.toLowerCase();

    // Determine the route based on status
    let routePrefix = "";

    switch (status) {
      case "created":
      case "overdue":
        routePrefix = "pending";
        break;
      case "assigned":
      case "accepted":
      case "in-progress":
      case "in progress":
        routePrefix = "assigned";
        break;
      case "completed":
        routePrefix = "completed";
        break;
      case "canceled":
      case "cancelled":
      case "approved":
        routePrefix = "history";
        break;
      default:
        // Default to pending for unknown statuses
        routePrefix = "pending";
    }

    navigate(
      `/ax/service-order/landing/${routePrefix}/view/${serviceType}/${row.id}`
    );
  };

  const handleDeleteClick = (row: any) => {
    if (row.statusDisplay !== "Created") {
      return; // Do nothing for Created status
    }
    setOrderToDelete(row);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = () => {
    if (orderToDelete) {
      cancelServiceOrdersMutation([orderToDelete.id], {
        onSuccess: () => {
          setSelectedOrders((prev) =>
            prev.filter((id) => id !== orderToDelete.id.toString())
          );
          setShowDeleteDialog(false);
          setOrderToDelete(null);
        },
      });
    }
  };

  const handleDeleteDialogClose = () => {
    setShowDeleteDialog(false);
    setOrderToDelete(null);
  };

  // Function to handle bulk cancel
  const handleBulkDeleteClick = (
    selectedData: ServiceOrderDisplayItem[],
    selectedIds: string[]
  ) => {
    if (selectedIds.length === 0) {
      return;
    }
    setOrdersToDelete(selectedData);
    setShowBulkDeleteDialog(true);
  };

  const handleConfirmBulkDelete = () => {
    if (ordersToDelete.length > 0) {
      const jobCardIds = ordersToDelete.map((order) => order.id);
      cancelServiceOrdersMutation(jobCardIds, {
        onSuccess: () => {
          setSelectedOrders([]);
          setShowBulkDeleteDialog(false);
          setOrdersToDelete([]);
        },
        onError: (error) => {
          console.error("Failed to cancel orders:", error);
        },
      });
    }
  };

  const handleBulkDeleteDialogClose = () => {
    setShowBulkDeleteDialog(false);
    setOrdersToDelete([]);
  };

  // Generic Table column configuration
  const tableColumns: TableColumn<any>[] = [
    {
      key: "jobCardNo",
      header: "SO ID",
      sortable: true,
      headerTooltip: "Unique, system-generated ID",
    },
    {
      key: "sopName",
      header: "SO Name",
      sortable: true,
      headerTooltip: "The name of the service order.",
    },
    {
      key: "sopAssociationTypeDisplay",
      header: "Service Association",
      sortable: true,
      headerTooltip:
        "The type of entity the order is for (Asset, System, Facility, Network).",
      type: "custom",
      render: (value: string, row: ServiceOrderDisplayItem) => (
        <div>
          <div className="ml-6">{value}</div>
        </div>
      ),
    },
    {
      key: "requestAccountNo",
      header: "Ref ID/Name",
      sortable: true,
      type: "custom",
      render: (value: string, row: ServiceOrderDisplayItem) => (
        <div>
          <div className="ml-6">{value}</div>
          <div className="text-sm text-gray-500 ml-6">
            {row.requestUsername}
          </div>
        </div>
      ),
      headerTooltip: "The ID and name of the specific entity.",
    },
    {
      key: "sopOperationTypeDisplay",
      header: "Type",
      sortable: true,
      headerTooltip:
        "The category of work or maintenance operation that this work order covers (e.g., Preventive Maintenance, Repair, Inspection, Emergency Response).",
    },
    {
      key: "createdDate",
      header: "Created Date",
      sortable: true,
      headerTooltip: "The date the service order was created.",
    },
    {
      key: "fieldForceName",
      header: "Assigned To",
      sortable: true,
      headerTooltip: "The field force member assigned to the order.",
    },
    {
      key: "priorityDisplay",
      header: "Priority",
      sortable: true,
      type: "badge",
      badgeConfig: {
        getClassName: (value: string) => getPriorityColor(value),
      },
      headerTooltip:
        "Indicates the urgency level of the service order (e.g., Low, Medium, High).",
    },
    {
      key: "expectedEndDate",
      header: "Due Date",
      sortable: true,
      headerTooltip: "The target completion date.",
    },
    {
      key: "statusDisplay",
      header: "Status",
      sortable: true,
      type: "badge", // This was missing!
      badgeConfig: {
        getClassName: getStatusBadgeClass,
      },
      headerTooltip: "The current status of the service order.",
    },
  ];

  // Table row actions
  const tableActions = [
    {
      label: "View Details",
      onClick: handleViewDetails,
      variant: "ghost" as const,
    },
    {
      label: "Edit",
      onClick: handleEditOrder,
      variant: "ghost" as const,
      disabled: (row: any) => row.statusDisplay !== "Created",
      className:
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none",
    }, // removed for now as per discussion
    {
      label: "Delete",
      onClick: handleDeleteClick,
      variant: "ghost" as const,
      disabled: (row: any) => row.statusDisplay !== "Created",
      className:
        "text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:pointer-events-none disabled:text-red-300",
    },
  ];

  // Bulk actions
  const bulkActions = [
    {
      label: "Delete Selected",
      onClick: handleBulkDeleteClick,
      icon: <AlertTriangle className="h-4 w-4" />,
      variant: "outline" as const,
    },
  ];

  const handleSelectionChange = (selectedIds: string[]) => {
    setSelectedOrders(selectedIds);
  };

  // Check if there are active filters
  const hasActiveFilters =
    currentSearch ||
    currentStatus ||
    currentPriority ||
    currentOrderType ||
    currentAssignedTo ||
    currentDateFrom ||
    currentDateTo;

  return (
    <div className="mx-auto space-y-6 bg-white">
      {/* Dashboard Metrics */}
      <ServiceOrderDashboard serviceOrders={summaryData} />

      <GenericTable
        data={serviceOrdersResponse?.results ?? []}
        columns={tableColumns}
        rowKey="id"
        selectable={true}
        selectedRows={selectedOrders}
        onSelectionChange={handleSelectionChange}
        actions={tableActions}
        actionsType="dropdown"
        bulkActions={bulkActions}
        // Search integration
        onSearch={handleSearch}
        searchPlaceholder="Search by Order ID or Name..."
        searchParamName="search"
        searchDebounceMs={300}
        // Filter panel integration
        filterPanel={
          <ServiceOrdersFilterPanel
            currentFilters={currentFilters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
          />
        }
        // Pagination
        pagination={pagination}
        onPageChange={handlePageChange}
        // Export
        // onExport={handleExport}
        // exportLoading={isExporting}
        emptyMessage="No service orders found matching your criteria"
        filters={
          hasActiveFilters ? (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Filters active
              </span>
            </div>
          ) : null
        }
      />
      <DeleteConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
        title="Cancel Service Order"
        itemName={orderToDelete?.id.toString()}
        description={`Are you sure you want to cancel service order "${orderToDelete?.sopName}"? This action cannot be undone.`}
        isLoading={isCancellingOrders}
      />

      <DeleteConfirmationDialog
        isOpen={showBulkDeleteDialog}
        onClose={handleBulkDeleteDialogClose}
        onConfirm={handleConfirmBulkDelete}
        title="Cancel Multiple Service Orders"
        itemName={`${ordersToDelete.length} service orders`}
        description={`Are you sure you want to cancel ${ordersToDelete.length} service orders? This action cannot be undone.`}
        isLoading={isCancellingOrders}
      />
    </div>
  );
};

export default ServiceOrdersTab;
